import React from 'react';
import {
  Stack,
  Alert,
  AlertTitle,
  Skeleton,
} from '@mui/material';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { TeamMember, Traveler } from '@marageti/z4-sdk/lib/people';
import { TripRequest, TripMatch } from '@marageti/z4-sdk/lib/travel';
import { TripMatchListCard } from '@marageti/z4-lib';
import TravelerCard from '../../traveler-card';
import TravelerProfileNotesSection from '../../traveler-profile-notes';
import TripRequestDetails from '../../trip-request-details';
import TripRequestMatchingNotes from '../../trip-request-matching-notes';
import RemindersSummary from '../../reminders-summary';
import TicketsSummary from '../../tickets-summary';
import { getChipsFromTripRequest } from '../../../utils/chips';

type TripRequestMessageSidebarViewProps = {
  error: ApiError | null;
  loggedInTeamMember: TeamMember | null;
  travelers: Traveler[] | undefined;
  travelerId: string | undefined;
  tripRequest: TripRequest | undefined;
  tripRequestTripMatches: TripMatch[] | undefined;
  onEditSuccess: () => void;
  refetchTicketCount?: () => void;
};

const TripRequestMessageSidebarView = ({
  error,
  loggedInTeamMember,
  travelers,
  travelerId,
  tripRequest,
  tripRequestTripMatches,
  onEditSuccess,
  refetchTicketCount,
}: TripRequestMessageSidebarViewProps) => (
  <Stack spacing={2}>
    {error && (
      <Alert severity="error">
        <AlertTitle>
          There was an error loading information
        </AlertTitle>
        {error.message}
        {error.code && ` Code: ${error.code} `}
        {error.id && ` ID: ${error.id}`}
      </Alert>
    )}
    <TravelerCard
      travelers={travelers || []}
      tripRequest={tripRequest}
      onEditProfile={onEditSuccess}
      onEditEmail={onEditSuccess}
    />
    {tripRequest && (
      <TripRequestDetails
        loggedInTeamMember={loggedInTeamMember}
        chips={getChipsFromTripRequest(tripRequest)}
        tripRequest={tripRequest}
        title="Trip Details"
        onEditSuccess={onEditSuccess}
      />
    )}
    {travelerId ? (
      <TravelerProfileNotesSection travelerId={travelerId} />
    ) : (
      <Skeleton variant="rounded" height={160} />
    )}
    {tripRequest && (
      <RemindersSummary
        tripRequestId={tripRequest.id}
        travelerId={travelers?.[0]?.id}
        emptyMessage="There are no reminders for this trip request."
      />
    )}
    {tripRequest && (
      <TicketsSummary
        tripRequestId={tripRequest.id}
        travelerId={travelers?.[0]?.id}
        travelerName={travelers?.[0]?.fullName}
        travelerEmail={travelers?.[0]?.email}
        emptyMessage="There are no tickets for this trip."
        refetchTicketCount={refetchTicketCount}
      />
    )}
    <TripMatchListCard
      matches={tripRequestTripMatches || []}
    />
    <TripRequestMatchingNotes
      tripRequest={tripRequest}
      onEditSuccess={onEditSuccess}
    />
  </Stack>
);

export default TripRequestMessageSidebarView;
